/**
 * Iter 95 — Public APK install / download landing page.
 *
 * Route: `/install` and `/download` (both public, no auth needed).
 *
 * Auto-detects the visitor's platform and surfaces the right action:
 *   - Android  → big "Install Now" button → /api/app/download/apk
 *   - iOS      → "Use web app" CTA (no native build yet)
 *   - Desktop  → QR code (scan with phone) + secondary "Open Web App" link
 *
 * Fetches build metadata from /api/app/manifest so version/notes auto-update
 * as new APKs are uploaded.
 */
import React, { useEffect, useState } from 'react';
import { Smartphone, Download, ShieldCheck, Loader2, Globe, ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';

const PLAY_STORE_FALLBACK = 'https://play.google.com/store/apps/details?id=com.elloraepc.erp';

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'desktop';
}

function bytesToMb(b) {
  if (!b) return '—';
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function InstallApp() {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);
  const [platform] = useState(detectPlatform());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    // Always use the current host (so /install on production hits production
    // backend, /install on preview hits preview backend). Iter 104 fix —
    // process.env.REACT_APP_BACKEND_URL is baked at build time and was pinning
    // production APK downloads to the preview host.
    const API = (typeof window !== 'undefined' && window.location?.origin) || process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/app/manifest`)
      .then(r => r.json())
      .then(setManifest)
      .catch(e => setError(e.message || 'Could not load build info'));
  }, []);

  const origin = (typeof window !== 'undefined' && window.location?.origin) || process.env.REACT_APP_BACKEND_URL || '';
  const apkAbsoluteUrl = manifest?.apk_url ? `${origin}${manifest.apk_url}` : null;
  // CRIT-04: APK downloads now require authentication. The desktop QR therefore
  // points to the /install PAGE (not the raw binary) so the phone opens it, the
  // user logs in, and the download happens via an authenticated request — no
  // token is ever embedded in a URL or QR code (avoids the HIGH-05 exposure).
  const installPageUrl = `${origin}/install`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(installPageUrl)}`;

  // Authenticated blob download (Bearer token in header, never in the URL).
  const handleDownload = async () => {
    if (!manifest?.apk_url) return;
    const token = (typeof window !== 'undefined') ? localStorage.getItem('access_token') : null;
    if (!token) {
      window.location.href = `/login?next=${encodeURIComponent('/install')}`;
      return;
    }
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(`${origin}${manifest.apk_url}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) {
        window.location.href = `/login?next=${encodeURIComponent('/install')}`;
        return;
      }
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = manifest.apk_filename || `ellora-epc-${manifest.version_name || 'latest'}.apk`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setError(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1325] text-white" data-testid="install-app-page">
      {/* Top brand bar */}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-[#3b4872] to-[#2c365a] flex items-center justify-center font-display font-black text-base">E</div>
          <div>
            <div className="font-display font-black text-sm tracking-widest">ELLORA EPC</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Construction ERP</div>
          </div>
        </div>
        <a href="/login" className="text-xs uppercase tracking-wider text-white/70 hover:text-white" data-testid="login-link">
          Already have an account? →
        </a>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Left — hero + install CTA */}
          <section>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-emerald-500/15 text-emerald-300 text-[10px] font-display font-bold uppercase tracking-widest mb-4" data-testid="version-badge">
              <CheckCircle2 size={11} />
              {manifest ? `v${manifest.version_name} · stable` : 'Loading build info…'}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] mb-4">
              Install Ellora EPC<br />
              <span className="text-white/60">on your Android device.</span>
            </h1>
            <p className="text-white/70 text-sm leading-relaxed mb-8 max-w-md">
              The full ERP in your pocket — Projects, HRMS, Payroll, Attendance with biometric punch,
              CRM, Tasks & Reports. Works offline-first for field staff.
            </p>

            {error && (
              <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm rounded-sm px-4 py-3 mb-6 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5" />
                <div>Could not load the latest build info — {error}.</div>
              </div>
            )}

            {/* Platform-specific CTA */}
            {platform === 'android' && manifest && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="inline-flex items-center gap-3 px-6 py-4 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-display font-black uppercase tracking-widest text-sm transition-all hover:translate-y-[-1px] hover:shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)] disabled:opacity-60"
                data-testid="install-android-btn"
              >
                {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                {downloading ? 'Downloading…' : 'Install Now'}
                <span className="opacity-60 text-[11px] font-mono ml-2">{bytesToMb(manifest.apk_size_bytes)}</span>
              </button>
            )}

            {platform === 'ios' && (
              <div className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-sm px-4 py-3 text-sm">
                  iOS native app is coming soon. For now, please use the web app — it's fully responsive on iPhone & iPad.
                </div>
                <a href="https://elloraepc.in" className="inline-flex items-center gap-2 px-5 py-3 rounded-sm bg-white text-[#0f1325] font-display font-bold uppercase tracking-widest text-sm" data-testid="open-web-app-ios">
                  <Globe size={16} /> Open Web App
                </a>
              </div>
            )}

            {platform === 'desktop' && manifest && (
              <div className="space-y-4">
                <p className="text-white/60 text-xs uppercase tracking-widest font-display font-bold">
                  📱 Scan this QR with your Android phone — it opens this page so you can log in and install
                </p>
                <div className="bg-white p-2 inline-block rounded-sm" data-testid="install-qr">
                  {qrUrl ? <img src={qrUrl} width="240" height="240" alt="Install QR code" /> : null}
                </div>
                <div className="text-[11px] text-white/40 max-w-[240px] font-mono break-all">
                  or open on your phone: {installPageUrl}
                </div>
              </div>
            )}

            {platform === 'unknown' && manifest && (
              <button onClick={handleDownload} disabled={downloading} className="inline-flex items-center gap-3 px-6 py-4 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-display font-black uppercase tracking-widest text-sm disabled:opacity-60" data-testid="install-fallback-btn">
                {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} {downloading ? 'Downloading…' : 'Download APK'}
              </button>
            )}

            {!manifest && !error && (
              <div className="inline-flex items-center gap-2 text-white/60 text-sm">
                <Loader2 size={16} className="animate-spin" /> Loading latest build…
              </div>
            )}

            {/* Install instructions for Android */}
            {platform === 'android' && (
              <ol className="mt-10 space-y-3 text-sm text-white/70" data-testid="install-instructions">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-sm bg-white/10 flex items-center justify-center text-[11px] font-display font-bold flex-shrink-0">1</span>
                  Tap <b className="text-white">Install Now</b> above → Chrome will download the APK.
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-sm bg-white/10 flex items-center justify-center text-[11px] font-display font-bold flex-shrink-0">2</span>
                  Open the downloaded file → if asked, enable <b className="text-white">"Allow from this source"</b> for Chrome.
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-sm bg-white/10 flex items-center justify-center text-[11px] font-display font-bold flex-shrink-0">3</span>
                  Tap <b className="text-white">Install</b> → wait ~10 sec → open the app & log in with your work email.
                </li>
              </ol>
            )}
          </section>

          {/* Right — release notes + trust signals */}
          <aside className="space-y-6">
            <div className="bg-white/[0.04] border border-white/10 rounded-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-display font-bold mb-3">What's new in {manifest ? `v${manifest.version_name}` : 'this build'}</div>
              {manifest?.release_notes?.length ? (
                <ul className="space-y-2 text-sm">
                  {manifest.release_notes.map((note, i) => (
                    <li key={i} className="flex gap-2.5">
                      <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                      <span className="text-white/80">{note}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-white/50 italic">Release notes loading…</div>
              )}
            </div>

            <div className="bg-white/[0.04] border border-white/10 rounded-sm p-5 space-y-3 text-xs text-white/60">
              <div className="flex items-start gap-2.5">
                <ShieldCheck size={14} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                <span>Signed with our private key — same fingerprint registered with Google Play.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Smartphone size={14} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                <span>Requires Android 7.0 (Nougat) or newer · {manifest ? bytesToMb(manifest.apk_size_bytes) : '~99 MB'}</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Globe size={14} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                <span>Package: <code className="text-white/80">{manifest?.package_id || 'com.elloraepc.erp'}</code></span>
              </div>
            </div>

            <a href="/login" className="block bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 rounded-sm p-5 transition-colors group" data-testid="open-web-app-aside">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-display font-bold mb-1">Prefer the browser?</div>
              <div className="text-sm flex items-center justify-between">
                <span className="text-white">Open the web app on this device</span>
                <ArrowRight size={14} className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </div>
            </a>
          </aside>
        </div>
      </main>

      <footer className="border-t border-white/10 px-6 py-6 text-center text-[11px] text-white/40">
        © Ellora EPC Pvt Ltd · <a href="/privacy" className="hover:text-white">Privacy</a> · <a href="/account-deletion" className="hover:text-white">Account deletion</a>
      </footer>
    </div>
  );
}
